import React, { useState } from 'react'
import {
  Button,
  Modal,
  Box,
  SpaceBetween,
  Input,
  FormField,
  Select,
  ButtonDropdown,
  Badge
} from '@cloudscape-design/components'

export interface FilterOption {
  label: string
  value: string
  count?: number
}

export interface FilterConfig {
  id: string
  type: 'select' | 'buttongroup'
  label: string
  options: FilterOption[]
  selectedOption: FilterOption | null
  onChange: (option: FilterOption | null) => void
  showCounts?: boolean
}

interface FilterAction {
  id: string
  label: string
  icon: string
  onClick: () => void
}

interface FilterButtonProps {
  searchPlaceholder?: string
  searchValue: string
  onSearchChange: (value: string) => void
  filters: FilterConfig[]
  onClearAll: () => void
  showClearAll?: boolean
  modalTitle?: string
  actions?: FilterAction[]
}

export const FilterButton: React.FC<FilterButtonProps> = ({
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  filters,
  onClearAll,
  showClearAll = true,
  modalTitle = "Filter Options",
  actions = []
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const activeFiltersCount = filters.filter(f => f.selectedOption).length
  const hasActiveFilters = activeFiltersCount > 0 || searchValue.length > 0

  const dropdownItems = [
    {
      text: "Filter Options",
      id: "filter-options",
      iconName: "filter"
    },
    ...(actions.map(action => ({
      text: action.label,
      id: action.id,
      iconName: action.icon
    }))),
    ...(showClearAll && hasActiveFilters ? [
      {
        text: "",
        id: "divider-1",
        itemType: "divider" as const
      },
      {
        text: "Clear All Filters",
        id: "clear-all",
        iconName: "close"
      }
    ] : [])
  ]

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs">
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={({ detail }) => onSearchChange(detail.value)}
          type="search"
          clearAriaLabel="Clear search"
        />
        <ButtonDropdown
          items={dropdownItems}
          onItemClick={({ detail }) => {
            if (detail.id === 'filter-options') {
              setIsModalOpen(true)
            } else if (detail.id === 'clear-all') {
              onClearAll()
            } else {
              const action = actions.find(a => a.id === detail.id)
              if (action) {
                action.onClick()
              }
            }
          }}
          variant="icon"
          ariaLabel="Filter and actions"
        >
          {hasActiveFilters && (
            <Badge color="blue">{activeFiltersCount + (searchValue ? 1 : 0)}</Badge>
          )}
        </ButtonDropdown>
      </SpaceBetween>

      <Modal
        visible={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        header={modalTitle}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              {showClearAll && hasActiveFilters && (
                <Button
                  variant="link"
                  onClick={() => {
                    onClearAll()
                    setIsModalOpen(false)
                  }}
                >
                  Clear All
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => setIsModalOpen(false)}
              >
                Apply
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {filters.map(filter => (
            <FormField key={filter.id} label={filter.label}>
              {filter.type === 'select' ? (
                <Select
                  selectedOption={filter.selectedOption}
                  onChange={({ detail }) => filter.onChange(detail.selectedOption)}
                  options={[
                    { label: 'All', value: 'all' },
                    ...filter.options
                  ]}
                  placeholder="Select option"
                />
              ) : (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant={!filter.selectedOption ? "primary" : "normal"}
                    onClick={() => filter.onChange(null)}
                  >
                    All
                  </Button>
                  {filter.options.map(option => (
                    <Button
                      key={option.value}
                      variant={filter.selectedOption?.value === option.value ? "primary" : "normal"}
                      onClick={() => filter.onChange(option)}
                    >
                      {option.label}
                      {filter.showCounts && option.count !== undefined && (
                        <span style={{ marginLeft: '4px' }}>
                          <Badge color="grey">
                            {option.count}
                          </Badge>
                        </span>
                      )}
                    </Button>
                  ))}
                </SpaceBetween>
              )}
            </FormField>
          ))}
        </SpaceBetween>
      </Modal>
    </>
  )
}